import { nonnull } from "@tento-core/assert";
import { ChartPointerStore, GraphData } from "@tento-lona/sheets/internal-ui";
import { LineChartBody } from "@tento-ui/components/line-chart/line-chart-body";
import { ChartViewModel } from "@tento-lona/platform";
import {
  DateFragment,
  DateTime,
  Duration,
  Time,
  TimeOfDay,
  TimezoneRegion,
} from "@tento-chrono";
import type { CalendarSubcolumnSpan } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { DataAliasPlugin } from "../../data.ts";
import { SheetViewModel } from "@tento-lona";
import { type SheetRow } from "@tento-lona";
import { SpanChart } from "@tento-ui/components/span/components/span-chart";
import { Span as ContentSpan } from "@tento-ui/components/span/span";
import type { SheetBinder } from "../../../../sheet/sheet-index-renderer.ts";

export namespace SpanDebugChart {
  export function makeSpan(
    data: GraphData.Collection,
    time: DateTime.Range,
    showOnHover: boolean = true,
  ): CalendarSubcolumnSpan {
    const xRange = {
      start: Math.floor(time.start.mse / Time.MS_PER_HR),
      end: Math.ceil(time.end.mse / Time.MS_PER_HR),
    };

    const $e = SpanChart.make();
    $e.toggleAttribute("hide-icon", true);
    // $e.style.background = "rgba(255, 0, 0, 0.1)";

    // Set CSS variables for visibility calculation
    $e.style.setProperty("--current-time-mse", Date.now().toString());
    $e.style.setProperty("--start", time.start.mse.toString());
    $e.style.setProperty("--end", time.end.mse.toString());

    const vm = new ChartViewModel(new ChartPointerStore(), data, true);

    const renderer: LineChartBody.Renderer[] = [
      new LineChartBody.Renderer.Points("default", 1),
      new LineChartBody.Renderer.RegressionCurve(),
      new LineChartBody.Renderer.RegressionBand(),
    ];
    $e.bindGraph(
      {
        xRange,
        vm,
        renderer,
        yPadding: 2,
        // options: {
        //   renderCrossHairs: false,
        // },
      },
      time.duration.toHrsF * 48,
    );

    return {
      id: `${time.start.toString()}-${time.end.toString()}`,
      bindContent: ContentSpan.staticContent($e),
      start: time.start,
      end: time.end,
      pointerEvents: showOnHover ? "all" : "none",
      item: null,
      callbacks: null,
    };
  }

  export const makeSpansFactory =
    (
      plugin: DataAliasPlugin,
      _sheet: SheetViewModel.Surface<SheetBinder>,
      _row: SheetRow.Joined.Handle,
    ) =>
    async (windowed: DateFragment.Windowed, _tz: TimezoneRegion) => {
      const graphData = plugin.context.store.getGraphDataForDebug();

      const start = windowed.fragment.date
        .withTime(windowed.validHours?.start)
        .withTz(windowed.fragment.tz);
      const time = new DateTime.Range(
        start,
        start.add(windowed.validHours?.duration ?? Duration.Time.DAY1),
      );

      const xRange = {
        start: Math.floor(time.start.mse / Time.MS_PER_HR),
        end: Math.ceil(time.end.mse / Time.MS_PER_HR),
      };

      const defaultRow = nonnull(graphData.rows.get("default"), "span-debug: the graph data has its default row");
      defaultRow.data.populate(xRange, xRange, [], "span-debug", null);

      const spans: CalendarSubcolumnSpan[] = [];
      spans.push(makeSpan(graphData, time, true));

      return spans;
    };
}
